// ============================================================================
// apps/web/src/lib/use-newsletter-count.ts
// ----------------------------------------------------------------------------
// Compteur d'inscriptions newsletter « new » (pas encore ajoutées à Brevo /
// non traitées) — alimente le badge de l'entrée « Newsletter » (sidebar +
// tiroir mobile), comme le badge Candidatures / To-Do.
//
// Le compteur MONTE à chaque inscription (status par défaut 'new') et REDESCEND
// quand elles sont marquées « ajouté » / « ignoré » dans la page Newsletter.
// Réservé aux détenteurs de `newsletter.manage` (RLS → 0 pour les autres).
//
// Rafraîchi : au montage, au focus / retour d'onglet, toutes les 60 s, et à la
// demande via `notifyNewsletterChanged()` (appelé par NewsletterPage après un
// changement de statut).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';
import { Capability } from '@magic/shared';

// Table absente des types générés → accès non typé (comme NewsletterPage).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase.from.bind(supabase) as unknown as (t: string) => any;

const EVENT = 'magic:newsletter-changed';

// À appeler après tout changement de statut pour rafraîchir le badge aussitôt.
export function notifyNewsletterChanged(): void {
  window.dispatchEvent(new Event(EVENT));
}

export function useNewNewsletterCount(): number {
  const { hasCapability } = useAuth();
  const canSee =
    hasCapability(Capability.NEWSLETTER_MANAGE) || hasCapability(Capability.ADMIN_ALL);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!canSee) { setCount(0); return; }
    const { count: c, error } = await db('newsletter_subscribers')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'new');
    if (!error) setCount(c ?? 0);
  }, [canSee]);

  useEffect(() => {
    if (!canSee) { setCount(0); return; }
    void refresh();
    const onFocus = () => { void refresh(); };
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onEvt = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(EVENT, onEvt);
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(EVENT, onEvt);
      window.clearInterval(id);
    };
  }, [canSee, refresh]);

  return count;
}
